import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { LogOut, ArrowLeft, Calendar, User, Mail, Phone, Bike, Home, Check, X, RefreshCw, Download, EyeOff, Eye, Archive } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminBookings = () => {
  const { isAdmin, logout, loading: authLoading } = useAdmin();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [hiddenBookings, setHiddenBookings] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, confirmed
  const [showArchive, setShowArchive] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingArchive, setExportingArchive] = useState(false);

  const adminPassword = localStorage.getItem('adminPassword') || '';

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/registrations/export.csv`, {
        headers: { 'X-Admin-Password': adminPassword },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `prenotazioni_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error', error);
      alert('Errore durante l\'esportazione. Riprova.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportArchiveCsv = async () => {
    setExportingArchive(true);
    try {
      const response = await axios.get(`${API}/registrations/hidden/export`, {
        headers: { 'X-Admin-Password': adminPassword },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      const stamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;
      a.download = `prenotazioni_archivio_${stamp}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export archive error', error);
      alert('Errore durante l\'esportazione archivio. Riprova.');
    } finally {
      setExportingArchive(false);
    }
  };

  const handleHideBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/registrations/${bookingId}/hide`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchBookings();
      fetchHiddenBookings();
    } catch (error) {
      console.error('Hide error', error);
      alert('Errore durante la rimozione dall\'elenco.');
    }
  };

  const handleUnhideBooking = async (bookingId) => {
    try {
      await axios.put(`${API}/registrations/${bookingId}/unhide`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchBookings();
      fetchHiddenBookings();
    } catch (error) {
      console.error('Unhide error', error);
      alert('Errore durante il ripristino.');
    }
  };

  const handleHideAllByEvent = async (eventId, eventTitle) => {
    if (!window.confirm(`Sei sicuro di voler nascondere TUTTE le iscrizioni di "${eventTitle}"? Potrai sempre recuperarle dall'archivio.`)) {
      return;
    }
    try {
      await axios.put(`${API}/registrations/hide-by-event/${eventId}`, {}, {
        headers: { 'X-Admin-Password': adminPassword }
      });
      fetchBookings();
      fetchHiddenBookings();
    } catch (error) {
      console.error('Hide by event error', error);
      alert('Errore durante la rimozione delle iscrizioni.');
    }
  };

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/admin/login');
      return;
    }
    if (!authLoading && isAdmin) {
      fetchBookings();
      fetchHiddenBookings();
    }
  }, [isAdmin, authLoading, navigate]);

  const fetchBookings = async () => {
    try {
      setDataLoading(true);
      const response = await axios.get(`${API}/registrations`);
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setBookings(sorted);
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setDataLoading(false);
    }
  };

  const fetchHiddenBookings = async () => {
    try {
      const response = await axios.get(`${API}/registrations/hidden`);
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setHiddenBookings(sorted);
    } catch (error) {
      console.error('Error fetching hidden bookings:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const updateBookingStatus = async (bookingId, newStatus, depositPaid, balancePaid) => {
    try {
      await axios.put(`${API}/registrations/${bookingId}`, {
        status: newStatus,
        deposit_paid: depositPaid,
        balance_paid: balancePaid
      });
      fetchBookings(); // Refresh list
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Errore durante l\'aggiornamento');
    }
  };

  const getFilteredBookings = () => {
    if (filter === 'all') return bookings;
    return bookings.filter(b => b.status === filter);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
        return <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-semibold">Confermato</span>;
      case 'cancelled':
        return <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">Annullato</span>;
      default:
        return <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">In attesa</span>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  // Show loading state while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Caricamento...</p>
        </div>
      </div>
    );
  }

  // If not logged in, show message and redirect link
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Accesso Richiesto</h2>
          <p className="text-gray-600 mb-6">Devi effettuare il login per accedere a questa pagina.</p>
          <Link 
            to="/admin/login" 
            className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors inline-block"
          >
            Vai al Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-600 text-white py-6 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">Prenotazioni</h1>
              <p className="text-blue-100 mt-1">Gestisci le iscrizioni agli eventi</p>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              data-testid="logout-btn"
            >
              <LogOut className="mr-2" size={20} />
              Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation & Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <Link
            to="/admin/dashboard"
            className="flex items-center text-blue-600 hover:text-blue-800 font-semibold transition-colors"
            data-testid="back-to-dashboard-btn"
          >
            <ArrowLeft className="mr-2" size={20} />
            Torna alla Dashboard
          </Link>
          
          <div className="flex items-center gap-4 flex-wrap">
            <button
              onClick={handleExportCsv}
              disabled={bookings.length === 0 || exporting}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                bookings.length === 0 || exporting
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
              data-testid="export-csv-btn"
              title="Scarica file CSV (apribile con Excel, Word, Google Fogli)"
            >
              <Download size={18} className="mr-2" />
              {exporting ? 'Esportando...' : 'Esporta CSV'}
            </button>

            <button
              onClick={() => setShowArchive(!showArchive)}
              className={`flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                showArchive
                  ? 'bg-amber-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              data-testid="show-archive-btn"
            >
              <Archive size={18} className="mr-2" />
              {showArchive ? 'Nascondi Archivio' : `Archivio (${hiddenBookings.length})`}
            </button>

            <button
              onClick={() => { fetchBookings(); fetchHiddenBookings(); }}
              className="flex items-center text-gray-600 hover:text-blue-600 transition-colors"
              data-testid="refresh-btn"
            >
              <RefreshCw size={20} className="mr-1" />
              Aggiorna
            </button>
            
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              data-testid="status-filter"
            >
              <option value="all">Tutte ({bookings.length})</option>
              <option value="pending">In attesa ({bookings.filter(b => b.status === 'pending').length})</option>
              <option value="confirmed">Confermate ({bookings.filter(b => b.status === 'confirmed').length})</option>
              <option value="cancelled">Annullate ({bookings.filter(b => b.status === 'cancelled').length})</option>
            </select>
          </div>
        </div>

        {/* Archive Section */}
        {showArchive && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-amber-800 flex items-center">
                <Archive className="mr-2" size={24} />
                Archivio Iscrizioni Nascoste ({hiddenBookings.length})
              </h2>
              <button
                onClick={handleExportArchiveCsv}
                disabled={hiddenBookings.length === 0 || exportingArchive}
                className={`flex items-center px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  hiddenBookings.length === 0 || exportingArchive
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-amber-600 text-white hover:bg-amber-700'
                }`}
                data-testid="export-archive-csv-btn"
              >
                <Download size={18} className="mr-2" />
                {exportingArchive ? 'Esportando...' : 'Esporta Archivio CSV'}
              </button>
            </div>
            
            {hiddenBookings.length === 0 ? (
              <p className="text-amber-700">Nessuna iscrizione archiviata.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {hiddenBookings.map((booking) => (
                  <div key={booking.id} className="bg-white p-4 rounded-lg border border-amber-200 flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">{booking.name} {booking.surname}</p>
                      <p className="text-sm text-gray-600">{booking.event_title}</p>
                      <p className="text-xs text-gray-500">{formatDate(booking.created_at)}</p>
                    </div>
                    <button
                      onClick={() => handleUnhideBooking(booking.id)}
                      className="flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 text-sm"
                      title="Ripristina nell'elenco principale"
                    >
                      <Eye size={16} className="mr-1" />
                      Ripristina
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-gray-500 text-sm">Totale Prenotazioni</p>
            <p className="text-3xl font-bold text-gray-900">{bookings.length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-gray-500 text-sm">In Attesa</p>
            <p className="text-3xl font-bold text-yellow-600">{bookings.filter(b => b.status === 'pending').length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-gray-500 text-sm">Confermate</p>
            <p className="text-3xl font-bold text-green-600">{bookings.filter(b => b.status === 'confirmed').length}</p>
          </div>
          <div className="bg-white p-6 rounded-xl shadow-md">
            <p className="text-gray-500 text-sm">Annullate</p>
            <p className="text-3xl font-bold text-red-600">{bookings.filter(b => b.status === 'cancelled').length}</p>
          </div>
        </div>

        {/* Bookings List */}
        {dataLoading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Caricamento prenotazioni...</p>
          </div>
        ) : getFilteredBookings().length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl shadow-md">
            <Calendar size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-xl text-gray-600 mb-2">Nessuna prenotazione trovata</p>
            <p className="text-gray-500">Le prenotazioni appariranno qui quando qualcuno si iscrive a un evento</p>
          </div>
        ) : (
          <div className="space-y-6">
            {getFilteredBookings().map((booking) => (
              <div 
                key={booking.id} 
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
                data-testid={`booking-card-${booking.id}`}
              >
                <div className="p-6">
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1">
                        {booking.name} {booking.surname}
                      </h3>
                      <p className="text-blue-600 font-semibold">{booking.event_title}</p>
                      <p className="text-sm text-gray-500 mt-1">
                        Iscritto il: {formatDate(booking.created_at)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(booking.status)}
                    </div>
                  </div>

                  {/* Details Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center text-gray-700">
                      <Mail size={18} className="mr-2 flex-shrink-0 text-blue-500" />
                      <a href={`mailto:${booking.email}`} className="text-sm hover:text-blue-600 truncate">
                        {booking.email}
                      </a>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Phone size={18} className="mr-2 flex-shrink-0 text-green-500" />
                      <a href={`https://wa.me/${booking.whatsapp?.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm hover:text-green-600">
                        {booking.whatsapp}
                      </a>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Bike size={18} className="mr-2 flex-shrink-0 text-orange-500" />
                      <span className="text-sm">{booking.motorcycle_choice}</span>
                    </div>
                    <div className="flex items-center text-gray-700">
                      <Home size={18} className="mr-2 flex-shrink-0 text-purple-500" />
                      <span className="text-sm">{booking.room_type} {booking.roommate && `(con ${booking.roommate})`}</span>
                    </div>
                  </div>

                  {/* Additional Details */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Data nascita:</span>
                        <p className="font-medium">{booking.birth_date}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Luogo nascita:</span>
                        <p className="font-medium">{booking.birth_place}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Passaporto:</span>
                        <p className="font-medium">{booking.passport_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Taglia T-shirt:</span>
                        <p className="font-medium">{booking.tshirt_size}</p>
                      </div>
                      {booking.nickname && (
                        <div>
                          <span className="text-gray-500">Nickname:</span>
                          <p className="font-medium">{booking.nickname}</p>
                        </div>
                      )}
                      {booking.food_intolerance && booking.food_intolerance !== 'Nothing' && (
                        <div>
                          <span className="text-gray-500">Intolleranze:</span>
                          <p className="font-medium text-red-600">
                            {booking.food_intolerance}
                            {booking.food_intolerance === 'Other' && booking.food_intolerance_details && (
                              <> — {booking.food_intolerance_details}</>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Payment Status & Actions */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={booking.deposit_paid}
                          onChange={(e) => updateBookingStatus(booking.id, booking.status, e.target.checked, booking.balance_paid)}
                          className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Acconto pagato</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={booking.balance_paid}
                          onChange={(e) => updateBookingStatus(booking.id, booking.status, booking.deposit_paid, e.target.checked)}
                          className="w-5 h-5 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Saldo pagato</span>
                      </label>
                    </div>
                    
                    <div className="flex gap-2">
                      {booking.status !== 'confirmed' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'confirmed', booking.deposit_paid, booking.balance_paid)}
                          className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors text-sm"
                          data-testid={`confirm-btn-${booking.id}`}
                        >
                          <Check size={16} className="mr-1" />
                          Conferma
                        </button>
                      )}
                      {booking.status !== 'cancelled' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'cancelled', booking.deposit_paid, booking.balance_paid)}
                          className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors text-sm"
                          data-testid={`cancel-btn-${booking.id}`}
                        >
                          <X size={16} className="mr-1" />
                          Annulla
                        </button>
                      )}
                      {booking.status !== 'pending' && (
                        <button
                          onClick={() => updateBookingStatus(booking.id, 'pending', booking.deposit_paid, booking.balance_paid)}
                          className="flex items-center bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors text-sm"
                          data-testid={`pending-btn-${booking.id}`}
                        >
                          In attesa
                        </button>
                      )}
                      <button
                        onClick={() => handleHideBooking(booking.id)}
                        className="flex items-center bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors text-sm"
                        data-testid={`hide-btn-${booking.id}`}
                        title="Nascondi dall'elenco (sarà disponibile nell'archivio)"
                      >
                        <EyeOff size={16} className="mr-1" />
                        Nascondi
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminBookings;
